<template>
    <Modal id="dojo-terminals-modal"
           icon="fa-solid fa-tablet"
           :title="title">
        <div v-if="errorMessage"
             class="alert alert-danger">
            <p>{{ errorMessage }}</p>
        </div>

        <div v-if="!errorMessage">
            <PageLoadingSpinner v-if="loading"></PageLoadingSpinner>

            <table v-else
                   class="table table-striped text-left table--valign-middle">
                <thead>
                    <tr>
                        <th>{{ l('DOJO_SETTINGS.TERMINALS_TID') }}</th>
                        <th>{{ l('DOJO_SETTINGS.TERMINALS_CURRENCY') }}</th>
                        <th>{{ l('DOJO_SETTINGS.TERMINALS_STATUS') }}</th>
                        <th>&nbsp;</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="terminal in terminals"
                        :key="terminal.lineNumber">
                        <td v-html="displayTid(terminal.tid)"></td>
                        <td>{{ terminal.currency }}</td>
                        <td>{{ terminal.status }}</td>
                        <td class="text-right">
                            <button v-if="terminal.status === 'AVAILABLE' && terminal.tid !== terminalId"
                                    class="btn btn-primary"
                                    data-testid="dojo-select-terminal-btn"
                                    @click="selectTerminal(terminal.tid)">
                                {{ l('DOJO_SETTINGS.TERMINALS_SELECT') }}
                            </button>
                            <span v-else-if="terminal.tid === terminalId"
                                  class="nowrap">
                                <em>{{ l('DOJO_SETTINGS.CURRENT_TERMINAL') }}</em>
                                <SquareBtn color="danger"
                                           class="ml-05"
                                           data-testid="dojo-deselect-terminal-btn"
                                           @click="deselectTerminal()">
                                    <i class="btn-icon fa-solid fa-trash-can"></i>
                                </SquareBtn>
                            </span>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <template v-slot:footer>
            <div class="modl-footer">
                <SquareBtn size="lg"
                           class="cancel-btn"
                           @click="cancel">
                    {{ l('CANCEL') }}
                </SquareBtn>
            </div>
        </template>
    </Modal>
</template>

<script>
import Modal from '@/vue/components/Modals/Modal';
import PageLoadingSpinner from '@/vue/components/PageLoadingSpinner';
import SquareBtn from '@/vue/components/SquareBtn';
import { container } from '@/container';
import { ModalPropsMixin } from '@/vue/mixins/ModalPropsMixin';

export default {
    name: 'DojoTerminalsModal',

    components: { SquareBtn, Modal, PageLoadingSpinner },

    mixins: [
        ModalPropsMixin,
    ],

    props: {
        title: {
            type: String,
            default: 'DOJO_SETTINGS.TERMINALS_TITLE',
        },
        icon: {
            type: String,
            default: 'fa-rectangle-list',
        },
        terminalId: {
            type: String,
            default: null,
        },
    },

    data() {
        return {
            loading: true,
            terminals: [],
            errorMessage: null,
        };
    },

    created() {
        try {
            container.dojoDriver.init(false);
            container.dojoDriver.getTerminalList()
                .then((data) => {
                    this.loading = false;
                    if (data.terminals) {
                        this.terminals = data.terminals;
                    }
                }).catch((error) => {
                    this.errorMessage = error.message;
                });
        } catch (error) {
            this.errorMessage = error.message;
        }
    },

    methods: {
        async selectTerminal(terminalId) {
            container.dialogService
                .confirm('DOJO_SETTINGS.TERMINALS_SELECT_CONFIRM')
                .then((confirmed) => {
                    if (confirmed) {
                        return this.resolve(terminalId);
                    }
                    // The user cancelled
                    return null;
                });
        },

        deselectTerminal() {
            container.dialogService
                .confirm('DOJO_SETTINGS.TERMINALS_DESELECT_CONFIRM')
                .then((confirmed) => {
                    if (confirmed) {
                        return this.resolve(null);
                    }
                    // The user cancelled
                    return null;
                });
        },

        /**
         * Depending on config settings we can use virtual terminals for testing. Each terminal will give a different
         * reponse. If this is a virtual terminal show the helpful description.
         *
         * @param {string} tid
         *
         * @return {string}
         */
        displayTid(tid) {
            switch (tid) {
                case 'VCMDTSLSIP0':
                    return 'VCMDTSLSIP0<br /><small>(Simulates a successful chip and pin payment)</small>';
                case 'VCMDTSLDIP0':
                    return 'VCMDTSLDIP0<br /><small>(Simulates a declined chip and pin payment)</small>';
                case 'VCMDTSLSCD0':
                    return 'VCMDTSLSCD0<br /><small>(Simulates a contactless payment with device verification)</small>';
                case 'VCMDTSLSIS0':
                    return 'VCMDTSLSIS0<br /><small>(Simulates a signature payment)</small>';
                case 'VCMDTSLUIP0':
                    return 'VCMDTSLUIP0<br /><small>(Simulates an unsuccessful payment result)</small>';
                case 'VCMDTSLTIP0':
                    return 'VCMDTSLTIP0<br /><small>(Simulates a "TIMED_OUT" payment result)</small>';
                default:
                    return tid;
            }
        },
    },
};
</script>
